import styled, { keyframes } from "styled-components";

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(var(--r)); }
  50% { transform: translateY(-12px) rotate(calc(var(--r) + 3deg)); }
`;

const floatSlow = keyframes`
  0%, 100% { transform: translateY(0) rotate(var(--r)); }
  50% { transform: translateY(-8px) rotate(calc(var(--r) - 2deg)); }
`;

export const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: inherit;
  direction: ${(props) => (props.$isRTL ? "rtl" : "ltr")};
  position: relative;
  overflow: hidden;

  /* Same palette as main app – bg, bgSecondary, bgTertiary */
  background: ${(props) =>
    `linear-gradient(155deg, ${props.$colors.bg} 0%, ${props.$colors.bgSecondary} 35%, ${props.$colors.bgTertiary} 65%, ${props.$colors.bg} 100%)`};
  background-attachment: fixed;

  /* Soft orbs in accent tone (same as main page accent) */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 80% 50% at 15% 85%, ${(p) => p.$accentOrb} 0%, transparent 55%),
      radial-gradient(ellipse 60% 40% at 88% 15%, ${(p) => p.$accentOrb} 0%, transparent 50%),
      radial-gradient(ellipse 50% 50% at 50% 50%, ${(p) => p.$accentOrb} 0%, transparent 60%);
    pointer-events: none;
  }

  /* Subtle vignette */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 100% 80% at 50% 0%, transparent 45%, ${(p) => (p.$isDark ? "rgba(0,0,0,0.15)" : "rgba(0,0,0,0.04)")} 100%);
    pointer-events: none;
  }
`;

export const BackgroundDecor = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;

  .leaf {
    position: absolute;
    opacity: ${(props) => (props.$isDark ? 0.12 : 0.22)};
    will-change: transform;
  }

  .leaf.blur {
    filter: blur(40px);
    opacity: ${(props) => (props.$isDark ? 0.2 : 0.35)};
  }

  .leaf.animate {
    animation: ${float} 18s ease-in-out infinite;
  }

  .leaf.animate-slow {
    animation: ${floatSlow} 22s ease-in-out infinite;
  }

  .leaf-1 { top: 5%; left: 4%; width: 160px; height: 160px; --r: -20deg; }
  .leaf-2 { top: 12%; right: 6%; width: 110px; height: 110px; --r: 35deg; }
  .leaf-3 { bottom: 18%; left: 8%; width: 130px; height: 130px; --r: 10deg; }
  .leaf-4 { bottom: 12%; right: 10%; width: 95px; height: 95px; --r: -40deg; }
  .leaf-5 { top: 48%; left: 1%; width: 75px; height: 75px; --r: -55deg; }
  .leaf-6 { top: 42%; right: 2%; width: 85px; height: 85px; --r: 65deg; }
  .leaf-7 { top: 25%; left: 15%; width: 55px; height: 55px; --r: 15deg; }
  .leaf-8 { bottom: 30%; right: 18%; width: 65px; height: 65px; --r: -25deg; }
  .blur-1 { top: 10%; right: 15%; width: 200px; height: 200px; --r: 50deg; }
  .blur-2 { bottom: 25%; left: 5%; width: 180px; height: 180px; --r: -30deg; }
`;

export const LoginCard = styled.div`
  position: relative;
  z-index: 1;
  background: ${(props) => props.$cardBg};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid ${(props) => props.$colors.border};
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 380px;
  box-shadow: ${(props) =>
    props.$isDark
      ? "0 25px 60px -15px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.06)"
      : "0 32px 64px -12px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0,0,0,0.05)"};
`;

export const TopBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 24px;
`;

export const IconButton = styled.button`
  background: transparent;
  border: 1px solid ${(props) => props.$colors.border};
  color: ${(props) => props.$colors.textSecondary};
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background: ${(props) => props.$colors.bgTertiary};
    color: ${(props) => props.$colors.text};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const Logo = styled.div`
  text-align: center;
  margin-bottom: 32px;

  h1 {
    font-size: 20px;
    font-weight: 600;
    color: ${(props) => props.$colors.text};
    margin: 0;
  }

  p {
    color: ${(props) => props.$colors.textMuted};
    margin: 8px 0 0;
    font-size: 13px;
  }
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => props.$colors.textSecondary};
`;

export const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${(props) => props.$colors.border};
  border-radius: 6px;
  font-size: 14px;
  background: ${(props) => props.$colors.bgTertiary};
  color: ${(props) => props.$colors.text};
  transition: all 0.15s;
  text-align: ${(props) => (props.$isRTL ? "right" : "left")};
  direction: ltr;

  &:focus {
    outline: none;
    border-color: ${(props) => props.$colors.accent};
    background: ${(props) => props.$colors.bg};
  }

  &::placeholder {
    color: ${(props) => props.$colors.textMuted};
  }
`;

export const Button = styled.button`
  background: ${(props) => props.$colors.text};
  color: ${(props) => props.$colors.bg};
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  margin-top: 8px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ErrorMessage = styled.div`
  background: ${(props) => props.$colors.errorBg};
  border: 1px solid ${(props) => props.$colors.error}33;
  color: ${(props) => props.$colors.error};
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
  text-align: center;
`;

export const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${(props) => props.$colors.border};
  }

  span {
    padding: 0 12px;
    color: ${(props) => props.$colors.textMuted};
    font-size: 12px;
  }
`;

export const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 10px 16px;
  border: 1px solid ${(props) => props.$colors.border};
  border-radius: 6px;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => props.$colors.text};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${(props) => props.$colors.bgTertiary};
    border-color: ${(props) => props.$colors.borderLight};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const SignupLink = styled.p`
  text-align: center;
  margin-top: 20px;
  color: ${(props) => props.$colors.textMuted};
  font-size: 12px;

  a {
    color: ${(props) => props.$colors.accent};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid ${(props) => props.$colors.border};

  a {
    color: ${(props) => props.$colors.textMuted};
    text-decoration: none;
    font-size: 12px;

    &:hover {
      color: ${(props) => props.$colors.text};
    }
  }
`;
